import React, { useState } from "react";
import { api, money, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { HandCoins, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Configure a sponsor match pool on a campaign. Used by organizers and admins.
export default function MatchManager({ campaignId, match, onUpdate }) {
  const [sponsor, setSponsor] = useState(match?.sponsor_name || "");
  const [pool, setPool] = useState(match?.pool_total || "");
  const [multiplier, setMultiplier] = useState(match?.multiplier || 1);
  const [active, setActive] = useState(match?.active ?? true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!sponsor.trim()) return toast.error("Enter the sponsor's name.");
    if (!pool || Number(pool) <= 0) return toast.error("Enter a match pool amount.");
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/match`, {
        sponsor_name: sponsor.trim(), pool_total: Number(pool), multiplier: Number(multiplier), active,
      });
      onUpdate?.(data.match);
      toast.success("Sponsor match saved.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/campaigns/${campaignId}/match`);
      setActive(false);
      onUpdate?.({ ...match, active: false });
      toast.success("Match deactivated.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const matched = Number(match?.matched_amount || 0);
  const total = Number(match?.pool_total || 0);
  const pct = total > 0 ? Math.min(100, Math.round((matched / total) * 100)) : 0;

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="match-manager">
      <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
        <HandCoins className="h-4 w-4 text-ff-green" /> Sponsor match
      </div>
      <p className="mt-1 text-xs text-ff-medium">Every donation is matched (up to the pool) and counts toward the goal — a proven way to boost giving.</p>

      {total > 0 && (
        <div className="mt-3 rounded-lg bg-[#ECFDF5] p-3" data-testid="match-progress">
          <div className="flex justify-between text-xs font-bold text-ff-green">
            <span>{money(matched)} matched</span><span>of {money(total)} ({pct}%)</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white">
            <div className="h-full rounded-full bg-ff-green transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        <div><Label className="text-ff-blue">Sponsor name</Label><Input data-testid="match-sponsor" value={sponsor} onChange={(e) => setSponsor(e.target.value)} placeholder="e.g. Acme Corporation" className="mt-1.5" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-ff-blue">Match pool ($)</Label><Input type="number" min="1" data-testid="match-pool" value={pool} onChange={(e) => setPool(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-ff-blue">Multiplier</Label><Input type="number" min="1" step="0.5" data-testid="match-multiplier" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} className="mt-1.5" /></div>
        </div>
        <label className="flex items-center gap-2.5 text-sm text-ff-dark">
          <Switch checked={active} onCheckedChange={setActive} data-testid="match-active" /> Match active
        </label>
      </div>

      <div className="mt-4 flex gap-2">
        <Button onClick={save} disabled={busy} data-testid="match-save" className="bg-ff-blue text-white hover:bg-ff-blue/90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save match"}
        </Button>
        {match?.active && (
          <Button variant="outline" onClick={clear} disabled={busy} data-testid="match-clear" className="border-ff-red text-ff-red hover:bg-ff-red hover:text-white">
            <Trash2 className="mr-1.5 h-4 w-4" /> Deactivate
          </Button>
        )}
      </div>
    </div>
  );
}
